import { baseApi } from './baseApi';

// Tipos para impresión
export interface PrintJobResult {
  jobId: string;
  receiptId: string;
  status: string;
  queuedAt: string;
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string;
  copies: number;
  printerName: string;
}

export interface PrintReceiptRequest {
  receiptData: any;
  printerConfigId?: string;
}

export interface PreviewReceiptResponse {
  formattedReceipt: string;
}

// Tipos básicos de PrinterConfig para compatibilidad
export interface PrinterConfig {
  id: string;
  name: string;
  type: string;
  connectionType: string;
  ipAddress?: string;
  port?: number;
  isDefault: boolean;
  openCashDrawer?: boolean;
  printNvLogo?: boolean;
  // Último estado reportado por el Print Agent:
  // ONLINE | OFFLINE | PAPER_OUT | PAPER_LOW | COVER_OPEN | ERROR | UNKNOWN
  lastStatus?: string | null;
  lastStatusAt?: string | null;
}

export interface CreatePrinterConfigRequest {
  name: string;
  printerType: string;
  connectionType: string;
  ipAddress?: string;
  port?: number;
}

export interface UpdatePrinterConfigRequest {
  name?: string;
  printerType?: string;
  connectionType?: string;
  ipAddress?: string;
  port?: number;
  isDefault?: boolean;
  printNvLogo?: boolean;
}

export const printersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ⚠️ DEPRECATED - Estos endpoints solo funcionan con backend on-premise
    // En producción (Render), usar Print Agent o Bluetooth para impresión local

    getPrinterConfigurations: builder.query<PrinterConfig[], void>({
      query: () => '/printers/configurations',
      providesTags: ['PrinterConfig'],
    }),

    getPrinterConfiguration: builder.query<PrinterConfig, string>({
      query: (id) => `/printers/configurations/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'PrinterConfig', id }],
    }),

    createPrinterConfiguration: builder.mutation<PrinterConfig, CreatePrinterConfigRequest>({
      query: (newConfig) => ({
        url: '/printers/configurations',
        method: 'POST',
        body: newConfig,
      }),
      invalidatesTags: ['PrinterConfig'],
    }),

    updatePrinterConfiguration: builder.mutation<PrinterConfig, { id: string } & UpdatePrinterConfigRequest>({
      query: ({ id, ...patch }) => ({
        url: `/printers/configurations/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PrinterConfig', id },
        'PrinterConfig'
      ],
    }),

    deletePrinterConfiguration: builder.mutation<void, string>({
      query: (id) => ({
        url: `/printers/configurations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PrinterConfig'],
    }),

    // Graba el logo de Branding en la memoria NV de la impresora (job FS q vía cloud print)
    flashPrinterLogo: builder.mutation<{ jobId: string }, string>({
      query: (id) => ({
        url: `/printers/configurations/${id}/flash-logo`,
        method: 'POST',
      }),
    }),

    printReceipt: builder.mutation<PrintJobResult, PrintReceiptRequest>({
      query: (request) => ({
        url: '/printers/print-receipt',
        method: 'POST',
        body: request,
      }),
    }),

    previewReceipt: builder.mutation<PreviewReceiptResponse, PrintReceiptRequest>({
      query: (request) => ({
        url: '/printers/preview-receipt',
        method: 'POST',
        body: request,
      }),
    }),

    getDefaultPrinter: builder.query<PrinterConfig, void>({
      query: () => '/printers/default-printer',
      providesTags: ['PrinterConfig'],
    }),

    // Kitchen Order Printing
    printKitchenOrder: builder.mutation<PrintJobResult, { orderId: string; printerConfigId?: string }>({
      query: (request) => ({
        url: '/printers/print-kitchen-order',
        method: 'POST',
        body: { orderId: request.orderId, printerConfigId: request.printerConfigId },
      }),
    }),
  }),
});

export const {
  useGetPrinterConfigurationsQuery,
  useGetPrinterConfigurationQuery,
  useCreatePrinterConfigurationMutation,
  useUpdatePrinterConfigurationMutation,
  useDeletePrinterConfigurationMutation,
  useFlashPrinterLogoMutation,
  usePrintReceiptMutation,
  usePreviewReceiptMutation,
  useGetDefaultPrinterQuery,
  usePrintKitchenOrderMutation,
} = printersApi;
